import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white'
      }`}
    >
      <nav className="max-w-full px-6 md:px-12 lg:px-16">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo - Left aligned comme Satisfy */}
          <div className="flex-shrink-0">
            <Link 
              to="/" 
              className="text-2xl md:text-3xl font-bold text-black tracking-[0.2em] font-eurostile uppercase hover:opacity-70 transition-opacity duration-300"
            >
              PODIUM.CC
            </Link>
          </div>

          {/* Desktop Navigation - Centered comme Satisfy */}
          <nav className="hidden lg:flex items-center justify-center flex-1 mx-12">
            <div className="flex items-center space-x-10">
              <a 
                href="#shop" 
                className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-xs tracking-widest"
              >
                Boutique
              </a>
              <Link 
                to="/configurateur" 
                className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-xs tracking-widest"
              >
                Configurateur
              </Link>
              
              {/* Services Dropdown - Opens on Hover */}
              <div className="relative group">
                <button 
                  className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-xs tracking-widest flex items-center gap-1"
                >
                  Services
                  <svg 
                    className="w-3 h-3 transition-transform duration-200 group-hover:rotate-180" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu - Shows on Hover */}
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link 
                    to="/etude-posturale" 
                    className="block px-4 py-3 text-black hover:bg-gray-50 transition-colors duration-200 font-eurostile uppercase text-xs tracking-widest"
                  >
                    Études Posturales
                  </Link>
                  <Link 
                    to="/location" 
                    className="block px-4 py-3 text-black hover:bg-gray-50 transition-colors duration-200 font-eurostile uppercase text-xs tracking-widest"
                  >
                    Location Vélo
                  </Link>
                  <Link 
                    to="/traces-gpx" 
                    className="block px-4 py-3 text-black hover:bg-gray-50 transition-colors duration-200 font-eurostile uppercase text-xs tracking-widest"
                  >
                    Traces GPX
                  </Link>
                  <Link 
                    to="/lavage-velo" 
                    className="block px-4 py-3 text-black hover:bg-gray-50 transition-colors duration-200 font-eurostile uppercase text-xs tracking-widest"
                  >
                    Lavage Vélo
                  </Link>
                  <Link 
                    to="/atelier-velo" 
                    className="block px-4 py-3 text-black hover:bg-gray-50 transition-colors duration-200 font-eurostile uppercase text-xs tracking-widest"
                  >
                    Atelier & Réparation
                  </Link>
                </div>
              </div>
              
              <Link 
                to="/contact" 
                className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-xs tracking-widest"
              >
                Infos & Contact
              </Link>
            </div>
          </nav>

          {/* Right side - Login & Cart icons */}
          <div className="hidden lg:flex items-center space-x-6">
            <button 
              className="text-black hover:opacity-50 transition-opacity duration-300"
              aria-label="Mon compte"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>
            <button 
              className="text-black hover:opacity-50 transition-opacity duration-300 relative"
              aria-label="Panier"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                0
              </span>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-black hover:opacity-50 transition-opacity duration-300"
            aria-label="Menu"
          >
            {isMenuOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu - Full screen overlay comme Satisfy */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
            <div className="flex flex-col px-6 py-8 space-y-6">
              <a 
                href="#shop" 
                className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-lg tracking-widest py-3 border-b border-gray-100"
                onClick={() => setIsMenuOpen(false)}
              >
                Boutique
              </a>
              <Link 
                to="/configurateur" 
                className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-lg tracking-widest py-3 border-b border-gray-100"
                onClick={() => setIsMenuOpen(false)}
              >
                Configurateur
              </Link>
              
              {/* Mobile Services Accordion */}
              <div className="border-b border-gray-100">
                <button 
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="w-full text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-lg tracking-widest py-3 flex justify-between items-center"
                >
                  Services
                  <svg 
                    className={`w-4 h-4 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isMobileServicesOpen && (
                  <div className="pl-4 pb-4 space-y-3">
                    <Link 
                      to="/etude-posturale" 
                      className="block text-gray-600 hover:text-black transition-colors duration-300 font-eurostile uppercase text-base tracking-widest py-2"
                      onClick={() => { setIsMenuOpen(false); setIsMobileServicesOpen(false); }}
                    >
                      Études Posturales
                    </Link>
                    <Link 
                      to="/location" 
                      className="block text-gray-600 hover:text-black transition-colors duration-300 font-eurostile uppercase text-base tracking-widest py-2"
                      onClick={() => { setIsMenuOpen(false); setIsMobileServicesOpen(false); }}
                    >
                      Location Vélo
                    </Link>
                    <Link 
                      to="/traces-gpx" 
                      className="block text-gray-600 hover:text-black transition-colors duration-300 font-eurostile uppercase text-base tracking-widest py-2"
                      onClick={() => { setIsMenuOpen(false); setIsMobileServicesOpen(false); }}
                    >
                      Traces GPX
                    </Link>
                    <Link 
                      to="/lavage-velo" 
                      className="block text-gray-600 hover:text-black transition-colors duration-300 font-eurostile uppercase text-base tracking-widest py-2"
                      onClick={() => { setIsMenuOpen(false); setIsMobileServicesOpen(false); }}
                    >
                      Lavage Vélo
                    </Link>
                    <Link 
                      to="/atelier-velo" 
                      className="block text-gray-600 hover:text-black transition-colors duration-300 font-eurostile uppercase text-base tracking-widest py-2"
                      onClick={() => { setIsMenuOpen(false); setIsMobileServicesOpen(false); }}
                    >
                      Atelier & Réparation
                    </Link>
                  </div>
                )}
              </div>
              
              <Link 
                to="/contact" 
                className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-lg tracking-widest py-3 border-b border-gray-100"
                onClick={() => setIsMenuOpen(false)}
              >
                Infos & Contact
              </Link>
              
              <div className="flex items-center space-x-6 pt-6">
                <button className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-sm tracking-widest">
                  Mon compte
                </button>
                <button className="text-black hover:opacity-50 transition-opacity duration-300 font-eurostile uppercase text-sm tracking-widest">
                  Panier (0)
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
